import { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { logger } from '../../logger';
import { newRelicAgent } from '../../utils/newrelic';
import { trackError } from '../../utils/errorTracking';

// Configure axios to log requests/responses
const requestLogDetails = (config: AxiosRequestConfig) => ({
  url: config.url,
  baseUrl: config.baseURL,
  params: config.params,
  method: config.method,
  data: config.data,
  headers: config.headers
});

const responseLogDetails = (response: AxiosResponse) => ({
  status: response.status,
  statusText: response.statusText,
  headers: response.headers,
  data: response.data,
  duration: new Date().valueOf() - (response.config as any).metadata.startTime.valueOf()
});

export const logRequestAndResponseDetails = (axios: AxiosInstance) => {
  axios.interceptors.request.use(
    config => {
      const { method, url, data } = config;

      newRelicAgent.recordCustomEvent('APIRequest', {
        method,
        url,
        timestamp: Date.now()
      });

      logger.debug('api:request', { method, url, data });
      return config;
    },
    error => {
      trackError({
        error,
        componentName: 'axios.request',
        additionalData: {
          type: 'request-error',
          config: requestLogDetails(error.config)
        }
      });
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    response => {
      const {
        config: { method, url },
        status,
        data
      } = response;

      // Only track non-200 responses as potential errors
      if (status >= 400) {
        trackError({
          error: new Error(`HTTP ${status}: ${response.statusText}`),
          componentName: 'axios.response',
          additionalData: {
            type: 'response-error',
            status,
            url,
            method,
            response: responseLogDetails(response)
          }
        });
      }

      newRelicAgent.recordCustomEvent('APIResponse', {
        method,
        url,
        status,
        timestamp: Date.now()
      });

      logger.debug('api:response', { method, url, status, data });
      return response;
    },
    (error: AxiosError) => {
      const { config, response } = error;

      trackError({
        error,
        componentName: 'axios.response',
        additionalData: {
          type: 'network-error',
          request: requestLogDetails(config),
          response: response ? responseLogDetails(response) : undefined
        }
      });

      logger.error('api:error', {
        method: config.method,
        url: config.url,
        status: response?.status,
        error: error.message,
        data: response?.data
      });
      return Promise.reject(error);
    }
  );
};
